/**
 * Zones whose rules changed in IANA tzdata 2026b-2026e. A runtime or browser
 * on older tzdata gives them the wrong UTC offset from the listed instant on,
 * so offsets for these zones come from this table rather than from `Intl`.
 */

/** A zone that keeps one UTC offset from an instant on. */
export interface PermanentOffset {
  /** First instant at which older tzdata gives this zone a different offset. */
  fromMs: number;
  /** Minutes east of UTC the zone keeps from `fromMs` on. */
  offsetMinutes: number;
  /**
   * Whether the zone already had `offsetMinutes` before `fromMs`, so its
   * clocks did not move then and only older tzdata moves them.
   */
  isOffsetKept: boolean;
}

// TODO: [#6288](https://github.com/CodeinPlace/cip/issues/6288) - Remove once
// members' browsers ship tzdata 2026e or newer; until then runtimeTzdata.test.ts
// in functions keeps this table in step with the runtime's tzdata.
/**
 * The zones, keyed by IANA name. Every offset is a whole number of hours,
 * because {@link getDisplayTimeZone} maps it to an `Etc/GMT` zone.
 */
export const PERMANENT_OFFSETS: ReadonlyMap<string, PermanentOffset> = new Map([
  [
    'America/Vancouver',
    {fromMs: Date.UTC(2026, 10, 1, 9), offsetMinutes: -420, isOffsetKept: true},
  ],
  [
    'America/Edmonton',
    {fromMs: Date.UTC(2026, 10, 1, 8), offsetMinutes: -360, isOffsetKept: true},
  ],
  [
    'America/Inuvik',
    {fromMs: Date.UTC(2026, 10, 1, 8), offsetMinutes: -360, isOffsetKept: true},
  ],
  [
    'America/Winnipeg',
    {fromMs: Date.UTC(2026, 10, 1, 7), offsetMinutes: -300, isOffsetKept: true},
  ],
  [
    'Africa/Casablanca',
    {fromMs: Date.UTC(2026, 8, 20, 1), offsetMinutes: 0, isOffsetKept: false},
  ],
  [
    'Africa/El_Aaiun',
    {fromMs: Date.UTC(2026, 8, 20, 1), offsetMinutes: 0, isOffsetKept: false},
  ],
]);

/**
 * The UTC offset current tzdata gives `timeZone` at an instant, for the zones
 * whose rules older tzdata gets wrong.
 *
 * @param params.timeZone - IANA time zone.
 * @param params.instantMs - The instant, in epoch milliseconds.
 * @return Minutes east of UTC when `timeZone` is listed and `instantMs` is at
 *   or after its change (the half-open interval `[fromMs, ∞)`); `undefined`
 *   otherwise, meaning the runtime's own offset is right.
 * @throws Never.
 */
export function getPermanentOffsetMinutes({
  timeZone,
  instantMs,
}: {
  timeZone: string;
  instantMs: number;
}): number | undefined {
  const entry = PERMANENT_OFFSETS.get(timeZone);
  return entry !== undefined && instantMs >= entry.fromMs
    ? entry.offsetMinutes
    : undefined;
}

/**
 * The instant from which a zone keeps an offset it already had while older
 * tzdata still moves its clocks, such as western Canada's last fall-back.
 *
 * @param timeZone - IANA time zone.
 * @return Epoch milliseconds, or `undefined` for a zone with no such change.
 * @throws Never.
 */
export function getKeptOffsetFromMs(timeZone: string): number | undefined {
  const entry = PERMANENT_OFFSETS.get(timeZone);
  return entry?.isOffsetKept ? entry.fromMs : undefined;
}

/**
 * The zone to pass to `Intl.DateTimeFormat` when formatting an instant's
 * clock time for a reader in `timeZone`. For a listed zone at or after its
 * change this is the fixed-offset `Etc/GMT` zone with the current offset, so
 * the time is right whatever tzdata the browser has. Never use the result to
 * name the zone: it would read as a bare UTC offset.
 *
 * @param params.timeZone - The reader's IANA time zone.
 * @param params.instant - The instant being formatted.
 * @return `timeZone` itself, or an `Etc/GMT` zone such as `Etc/GMT+7`
 *   (POSIX sign: that is UTC-7).
 * @throws Never.
 */
export function getDisplayTimeZone({
  timeZone,
  instant,
}: {
  timeZone: string;
  instant: Date;
}): string {
  const offsetMinutes = getPermanentOffsetMinutes({
    timeZone,
    instantMs: instant.getTime(),
  });
  if (offsetMinutes === undefined) {
    return timeZone;
  }
  const hours = -offsetMinutes / 60;
  if (hours === 0) {
    return 'Etc/GMT';
  }
  return hours > 0 ? `Etc/GMT+${String(hours)}` : `Etc/GMT${String(hours)}`;
}
